'use client';

import React from 'react';
import { cn } from '@/lib/cn';

type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'danger'
    | 'success'
    | 'info'
    | 'warning'
    | 'accent'
    | 'primarySolid'
    | 'dangerSolid'
    | 'infoSolid';

type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    icon?: React.ReactNode;
}

const variants: Record<ButtonVariant, string> = {
    primary:
        'bg-white text-black hover:bg-zinc-200 border border-white/10',
    secondary:
        'bg-bg-surface text-white border border-border-default hover:bg-bg-elevated hover:border-border-strong',
    ghost:
        'bg-transparent text-zinc-400 hover:bg-bg-elevated hover:text-zinc-200 border border-transparent',
    danger:
        'bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 hover:border-rose-500/30',
    success:
        'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-500/30',
    info:
        'bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 hover:border-blue-500/30',
    warning:
        'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/30',
    accent:
        'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 hover:border-cyan-500/30',
    primarySolid:
        'bg-emerald-600 text-white hover:bg-emerald-500 border border-emerald-500/20',
    dangerSolid:
        'bg-rose-600 text-white hover:bg-rose-500 border border-rose-500/20',
    infoSolid:
        'bg-blue-600 text-white hover:bg-blue-500 border border-blue-500/20',
};

const sizes: Record<ButtonSize, string> = {
    sm: 'px-2.5 py-1.5 text-xs gap-1.5',
    md: 'px-3.5 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-sm gap-2.5',
    xl: 'px-8 py-6 text-xl gap-3',
    icon: 'p-2 min-w-[44px] min-h-[44px] md:min-w-[32px] md:min-h-[32px]',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
    variant = 'ghost',
    size = 'md',
    loading = false,
    icon,
    children,
    className = '',
    disabled,
    ...props
}, ref) {
    return (
        <button
            ref={ref}
            className={cn(
                'inline-flex items-center justify-center rounded-lg font-medium',
                'transition-colors duration-150',
                'active:scale-[0.98]',
                'focus-visible:ring-2 focus-visible:ring-zinc-500/50 focus-visible:outline-none',
                'disabled:opacity-50 disabled:pointer-events-none',
                variants[variant],
                sizes[size],
                className,
            )}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
            ) : icon ? (
                <span className="shrink-0">{icon}</span>
            ) : null}
            {children}
        </button>
    );
});
